import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Plus, Download, ArrowLeft, GitFork, MoreHorizontal, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { formatCardDate } from "@/design/format";
import {
  Button,
  Card,
  CardBody,
  Pill,
  EmptyState,
  FilterChips,
  SearchInput,
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
  NarrativeCard,
  HeimdallAttribution,
} from "@/design/components";
import { useReports, useReport, useSeed, useProjects, useProgramme, useActorId, useMoney, useAnalystName, usePeopleIndex } from "@/domain/hooks";
import { useTrace } from "@/shell/TraceProvider";
import { useCreateReport, useApproveReport } from "@/domain/useMutations";
import type { Report, ReportType } from "@/domain/types";

const TYPE_VARIANT: Record<ReportType, "brand" | "info" | "success" | "warn"> = {
  Impact: "brand",
  Update: "info",
  Progress: "success",
  Financial: "warn",
};

/** Resolve the base route prefix so links work under /stratus or /funder. */
function useReportsBase(): string {
  const { pathname } = useLocation();
  return pathname.startsWith("/funder") ? "/funder/reports" : "/stratus/reports";
}

// ---------------------------------------------------------------------------
// Gallery
// ---------------------------------------------------------------------------

export function ReportsGallery() {
  const reports = useReports();
  const base = useReportsBase();
  const analystName = useAnalystName();
  const people = usePeopleIndex();
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const filtered = reports.filter((r) => {
    if (type !== "all" && r.type !== type) return false;
    if (q && !`${r.title} ${r.periodLabel} ${r.audience}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const typeChips = [
    { value: "all", label: "All", count: reports.length || undefined },
    { value: "Impact", label: "Impact", count: reports.filter((r) => r.type === "Impact").length || undefined },
    { value: "Update", label: "Update", count: reports.filter((r) => r.type === "Update").length || undefined },
    { value: "Progress", label: "Progress", count: reports.filter((r) => r.type === "Progress").length || undefined },
    { value: "Financial", label: "Financial", count: reports.filter((r) => r.type === "Financial").length || undefined },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <FilterChips options={typeChips} value={type} onChange={setType} />
        <div className="flex items-center gap-2">
          <SearchInput value={q} onChange={setQ} placeholder="Search reports" className="w-56" />
          {/* Not in the MVP, come back later */}
          {/* <Button variant="secondary" size="sm" onClick={() => toast.success("Live view link copied: recipients see the current ledger, always up to date")}>
            Share live view
          </Button> */}
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" /> Create report
          </Button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title="No reports yet"
          body="Create a report to compose an impact, progress or financial summary from the programme's confirmed spend and evidence."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map((r) => (
            <Link key={r.id} to={`${base}/${r.id}`} className="group block">
              <Card className="overflow-hidden transition-colors group-hover:border-brand/50">
                <div className="relative h-28 bg-paper">
                  {r.coverImageUrl ? (
                    <img src={r.coverImageUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center justify-center">
                      <span className="text-meta text-mute">Impact Ledger</span>
                    </div>
                  )}
                  <span className="absolute left-2 top-2 flex gap-1.5">
                    <Pill variant={TYPE_VARIANT[r.type]}>{r.type}</Pill>
                    {r.kind === "visit" && <Pill variant="brand">Visit</Pill>}
                  </span>
                </div>
                <CardBody>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold leading-snug text-ink">{r.title}</p>
                    <button
                      className="-my-2 -mr-1 flex h-11 w-11 shrink-0 items-center justify-center rounded text-faint hover:bg-subtle hover:text-ink"
                      aria-label="Report actions"
                      onClick={(e) => { e.preventDefault(); toast.message("Report actions: duplicate, share, delete"); }}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-mute">{r.periodLabel} · {r.audience}</p>
                  {/* TODO(heimdall): AI analyst not built yet — re-enable the AI-draft attribution when Heimdall ships.
                  {r.draftedByAI && (
                    <HeimdallAttribution
                      name={analystName}
                      reviewerName={r.reviewedById ? people[r.reviewedById]?.fullName ?? "a reviewer" : undefined}
                      className="mt-1.5"
                    />
                  )}
                  */}
                  <div className="mt-3 flex items-center justify-between text-xs text-faint">
                    <span className="data">Edited {formatCardDate(r.updatedAt)}</span>
                    {r.limitedData && (
                      <span className="flex items-center gap-1 text-warn">
                        <AlertTriangle className="h-3 w-3" /> limited data
                      </span>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Link>
          ))}
        </div>
      )}

      <CreateReportDrawer open={createOpen} onOpenChange={setCreateOpen} base={base} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create report
// ---------------------------------------------------------------------------

function CreateReportDrawer({ open, onOpenChange, base }: { open: boolean; onOpenChange: (o: boolean) => void; base: string }) {
  const projects = useProjects();
  const programme = useProgramme();
  const actorId = useActorId();
  const analystName = useAnalystName();
  const navigate = useNavigate();
  const { create, isPending: creating } = useCreateReport();
  const [type, setType] = useState<ReportType>("Progress");
  const [scope, setScope] = useState<"programme" | "project">("programme");
  const [projectId, setProjectId] = useState(projects[0]?.id ?? "");
  const [audience, setAudience] = useState("Stratus Health oversight and the Danish Impact Fund");
  const [period, setPeriod] = useState("Q2 2026");
  const [notes, setNotes] = useState("");

  async function submit(draftedByAI = false) {
    const newId = await create({
      title: `${scope === "project" ? projects.find((p) => p.id === projectId)?.name ?? "Project" : programme.name} · ${period} ${type.toLowerCase()} report`,
      type,
      scope,
      projectId: scope === "project" ? projectId : undefined,
      audience,
      periodLabel: period,
      periodStart: "2026-04-01",
      periodEnd: "2026-06-30",
      authorId: actorId,
      notes,
      visibleTo: ["oversight", "funder", "contractor"],
      draftedByAI,
    });
    onOpenChange(false);
    toast.success(draftedByAI ? `${analystName} drafted the report from the verified record: review and approve it` : "Report composed from the programme's confirmed spend and evidence");
    if (newId) navigate(`${base}/${newId}`);
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange} width="560px">
      <DrawerHeader>
        <DrawerTitle className="text-base font-semibold text-ink">Create report</DrawerTitle>
        <p className="mt-0.5 text-sm text-mute">Compose a report from the live programme data.</p>
      </DrawerHeader>
      <DrawerBody className="space-y-4">
        <Field label="Report type">
          <NativeSelect value={type} onChange={(v) => setType(v as ReportType)}>
            {(["Impact", "Update", "Progress", "Financial"] as ReportType[]).map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </NativeSelect>
        </Field>
        <Field label="Scope">
          <NativeSelect value={scope} onChange={(v) => setScope(v as "programme" | "project")}>
            <option value="programme">Whole programme</option>
            <option value="project">A single project</option>
          </NativeSelect>
        </Field>
        {scope === "project" && (
          <Field label="Project">
            <NativeSelect value={projectId} onChange={setProjectId}>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </NativeSelect>
          </Field>
        )}
        <Field label="Target audience">
          <input value={audience} onChange={(e) => setAudience(e.target.value)} className="w-full rounded-md border border-line px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand" />
        </Field>
        <Field label="Reporting period">
          <input value={period} onChange={(e) => setPeriod(e.target.value)} className="w-full rounded-md border border-line px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand" />
        </Field>
        <Field label="Additional notes (optional)">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Anything specific to call out in the outlook section." className="w-full rounded-md border border-line px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand" />
        </Field>
        <NarrativeCard
          variant="briefing"
          title="Help us build your report"
          body="The report will pull in updates on completed work, the transaction log for the period, milestones achieved, and notable events, composed into an introduction, financials by category, evidence highlights and an outlook."
        />
      </DrawerBody>
      <DrawerFooter>
        <Button variant="ghost" disabled={creating} onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button variant="secondary" disabled={creating} onClick={() => submit(true)}>Draft with {analystName}</Button>
        <Button disabled={creating} onClick={() => submit(false)}>Create report</Button>
      </DrawerFooter>
    </Drawer>
  );
}

function NativeSelect({ value, onChange, children }: { value: string; onChange: (v: string) => void; children: React.ReactNode }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-line bg-card px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand"
    >
      {children}
    </select>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-faint">{label}</span>
      {children}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Report view
// ---------------------------------------------------------------------------

export function ReportView() {
  const { reportId = "" } = useParams();
  const report = useReport(reportId);
  const seed = useSeed();
  const money = useMoney();
  const base = useReportsBase();
  const { openTrace } = useTrace();
  const { approve: approveReport, isPending: approvingReport } = useApproveReport();
  const analystName = useAnalystName();
  const people = usePeopleIndex();
  // useProjects(), not seed.projects: seed is always the demo/fixture blob
  // (hydrated regardless of mode), whose project ids are fake slugs a real
  // trace lookup (traceApi.getProject, via openTrace below) would always
  // reject - "Trace source" on any programme-wide report (no single
  // report.projectId) silently failed in real mode for this reason. Called
  // unconditionally (not inside the ?? below) - it's a hook.
  const realProjects = useProjects();
  const traceProjectId = report?.projectId ?? realProjects.find((p) => p.spent > 0)?.id;

  const categoryRows = useMemo(() => {
    if (!report) return [];
    const items = report.projectId
      ? seed.spendItems.filter((it) => {
          const ask = seed.spendRequests.find((r) => r.id === it.spendRequestId);
          return ask?.projectId === report.projectId;
        })
      : seed.spendItems;
    const byCat = new Map<string, number>();
    for (const it of items) byCat.set(it.category ?? "Uncategorised", (byCat.get(it.category ?? "Uncategorised") ?? 0) + it.amount);
    return [...byCat.entries()].sort((a, b) => b[1] - a[1]);
  }, [report, seed]);

  const milestones = useMemo(() => {
    if (!report) return [];
    return report.projectId ? seed.milestones.filter((m) => m.projectId === report.projectId) : seed.milestones;
  }, [report, seed]);

  /** Provenance figures for the verification line: how many spend entries the report draws on, and how many carry evidence. */
  const provenance = useMemo(() => {
    if (!report) return { entries: 0, coverage: 0 };
    const items = report.projectId
      ? seed.spendItems.filter((it) => {
          const ask = seed.spendRequests.find((r) => r.id === it.spendRequestId);
          return ask?.projectId === report.projectId;
        })
      : seed.spendItems;
    const entries = items.length;
    const withEvidence = items.filter((it) => it.evidence.length > 0 || (it.evidenceIds?.length ?? 0) > 0).length;
    const coverage = entries === 0 ? 0 : Math.round((withEvidence / entries) * 100);
    return { entries, coverage };
  }, [report, seed]);

  if (!report) {
    return <EmptyState title="Report not found" body="This report may have been removed. Return to the gallery to pick another." />;
  }

  return (
    <div className="mx-auto max-w-3xl">
      <Link to={base} className="mb-4 inline-flex items-center gap-1 text-sm text-mute hover:text-ink">
        <ArrowLeft className="h-4 w-4" /> All reports
      </Link>

      <div className="rounded-card border border-line bg-paper p-6 sm:p-8">
      {report.coverImageUrl && (
        <img src={report.coverImageUrl} alt="" className="mb-5 h-44 w-full rounded-control border border-line object-cover" />
      )}

      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gold pb-4">
        <div>
          <div className="flex items-center gap-2">
            <Pill variant={TYPE_VARIANT[report.type]}>{report.type}</Pill>
            <span className="data text-xs text-faint">{report.periodLabel}</span>
          </div>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight text-ink">{report.title}</h1>
          <p className="mt-1 text-sm text-mute">
            For {report.audience} · created {formatCardDate(report.createdAt)}
          </p>
          <p className="data mt-2 text-[11px] uppercase tracking-wide text-faint">
            Verified record · {report.id} · {provenance.entries} entries · {provenance.coverage}% evidenced
          </p>
        </div>
        <div className="flex items-center gap-2">
          {traceProjectId && (
            <Button variant="ghost" size="sm" aria-label="Trace source: follow this report to its evidence" onClick={() => openTrace({ kind: "project", id: traceProjectId }, { from: "Reports" })}>
              <GitFork className="h-4 w-4" /> Trace source
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={() => toast.success("Report downloaded as PDF")}>
            <Download className="h-4 w-4" /> Download
          </Button>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2 rounded-md border border-line bg-subtle/50 px-3 py-2 text-xs text-mute">
        <span className="flex h-1.5 w-1.5 rounded-full bg-ok" aria-hidden />
        Reads from the live ledger. Figures are current; click a milestone or "Trace source" to follow any figure to its evidence.
      </div>

      {/* TODO(heimdall): AI analyst not built yet — re-enable the "Drafted by {analyst}"
          review banner and the AI-draft attribution when Heimdall ships.
      {report.draftedByAI && !report.reviewedById && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-brand/30 bg-brandSoft px-3 py-2">
          <span className="text-xs text-brand">Drafted by {analystName} from the verified record. Review the figures, then approve to file it.</span>
          <Button size="sm" disabled={approvingReport} onClick={async () => { await approveReport(report.id); toast.success("Report reviewed and filed"); }}>
            Approve and file
          </Button>
        </div>
      )}
      {report.draftedByAI && report.reviewedById && (
        <HeimdallAttribution
          name={analystName}
          reviewerName={people[report.reviewedById]?.fullName ?? "a reviewer"}
          className="mt-3"
        />
      )}
      */}

      {report.limitedData && (
        <div className="mt-4 flex items-center gap-2 rounded-md bg-warnSoft px-3 py-2 text-sm text-warn">
          <AlertTriangle className="h-4 w-4" /> This report was composed from limited seed data.
        </div>
      )}

      <article className="mt-6 space-y-6">
        {report.sections.map((s) => (
          <section key={s.heading}>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-faint">{s.heading}</h2>
            <p className="mt-1.5 text-sm leading-relaxed text-ink">{s.body}</p>

            {s.heading === "Financials by category" && categoryRows.length > 0 && (
              <div className="mt-3 overflow-hidden rounded-lg border border-line bg-card">
                <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-hairline text-left text-xs text-faint">
                      <th className="px-4 py-2 font-medium">Category</th>
                      <th className="px-4 py-2 text-right font-medium">Confirmed spend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryRows.map(([cat, amt]) => (
                      <tr key={cat} className="border-b border-hairline last:border-0">
                        <td className="px-4 py-2 text-ink">{cat}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-ink">{money(amt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            )}

            {s.heading === "Milestones" && milestones.length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {milestones.map((m) => (
                  <li key={m.id}>
                    <button aria-label={`Trace source for milestone ${m.name}`} onClick={() => openTrace({ kind: "milestone", id: m.id }, { from: "Reports" })} className="flex w-full items-center justify-between rounded-md border border-line bg-card px-3 py-2 text-sm transition-colors hover:border-strong hover:bg-subtle/60">
                      <span className="text-ink">{m.name}</span>
                      <Pill variant={m.status === "disbursed" ? "success" : m.status === "planned" ? "neutral" : "info"}>
                        {m.status.replace(/_/g, " ")}
                      </Pill>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </article>
      </div>
    </div>
  );
}
